"use client";

import React, { forwardRef, SelectHTMLAttributes } from "react";
import { cn } from "../lib/utils";
import { errorStyles, inputVariants } from "./input";

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  wrapperClassName?: string;
  label?: string;
  labelClassName?: string;
  /** `default` = the original pill select (wrapped w/ label). `box` = a bare,
   *  full-width rectangular scheme-tokened select for forms (host owns the
   *  label), matching Input variant="box". `pill` = a bare, full-width select
   *  wearing Input variant="pill", so it sits beside pill inputs on the
   *  marketing forms without a second shape appearing mid-form. `outline` =
   *  a bare, full-width select wearing Input variant="outline". */
  variant?: "default" | "box" | "pill" | "outline";
  /** Paints the error border on the `pill` and `outline` variants, same as
   *  Input's `error` prop — without it a required select has no visual way to
   *  show it was left blank. */
  error?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      className,
      children,
      onChange,
      wrapperClassName,
      label,
      labelClassName,
      variant = "default",
      error,
      ...props
    },
    ref,
  ) => {
    // Pill: the Input pill shape, borrowed rather than re-typed so the two
    // controls can never drift apart. Native arrow kept — no appearance-none.
    if (variant === "pill") {
      return (
        <select
          aria-invalid={error || undefined}
          className={cn(
            inputVariants({ variant: "pill" }),
            // The pill's uniform p-3 clips descenders inside a fixed-height
            // select; let the h-10 centre the text and leave room for the arrow.
            "w-full bg-white py-0 pl-3 pr-9",
            error && errorStyles.pill,
            className,
          )}
          ref={ref}
          onChange={onChange}
          {...props}
        >
          {children}
        </select>
      );
    }
    // Outline: the Input outline shape. The chevron is the forms plugin's
    // background image, so the right padding only has to clear it.
    if (variant === "outline") {
      return (
        <select
          aria-invalid={error || undefined}
          className={cn(
            inputVariants({ variant: "outline" }),
            "pr-9",
            error && errorStyles.outline,
            className,
          )}
          ref={ref}
          onChange={onChange}
          {...props}
        >
          {children}
        </select>
      );
    }
    // Box: a plain rectangular select, no forced wrapper/label — drops into
    // Field layouts that supply their own label.
    if (variant === "box") {
      return (
        <select
          className={cn(
            "text-scheme-text-primary focus-visible:ring-scheme-action h-10 w-full rounded-md border-0 bg-black/[0.04] px-3 text-sm transition-colors focus:outline-none focus-visible:outline-none focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50",
            className,
          )}
          ref={ref}
          onChange={onChange}
          {...props}
        >
          {children}
        </select>
      );
    }
    return (
      <div className={cn("flex justify-center", wrapperClassName)}>
        <label className={cn(labelClassName)} htmlFor="destination-select">
          {label}
        </label>
        <select
          name="destination-select"
          id="destination-select"
          className={cn(
            "shadow-douglas-fir focus:ring-douglas-fir border-input bg-background ring-offset-background placeholder:text-muted-foreground h-10 w-full max-w-xs appearance-none rounded-full border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            className,
          )}
          ref={ref}
          onChange={onChange}
          {...props}
        >
          {children}
        </select>
      </div>
    );
  },
);

Select.displayName = "Select";

export const SelectItem = forwardRef<
  HTMLOptionElement,
  React.OptionHTMLAttributes<HTMLOptionElement>
>(({ className, children, ...props }, ref) => {
  return (
    <option
      ref={ref}
      className={cn(
        "relative cursor-default select-none py-1.5 pl-8 pr-2",
        className,
      )}
      {...props}
    >
      {children}
    </option>
  );
});

SelectItem.displayName = "SelectItem";
